import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Settings as SettingsIcon, Save, Sprout } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import Navigation from '../components/Navigation';
import ResetTemporada from '../components/ResetTemporada';
import { getAdminHeaders, canEdit } from '../lib/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Configuracoes = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    presenca: 5,
    pontualidade: 5,
    biblia: 5,
    visita: 10,
    versiculo: 3,
    devocional: 3
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      setSettings(res.data);
    } catch (error) {
      console.error('Erro ao carregar configuracoes:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({ ...prev, [name]: parseInt(value) || 0 }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar valores
    for (const [key, value] of Object.entries(settings)) {
      if (value < 0) {
        toast.error(`Valor de ${key} não pode ser negativo`);
        return;
      }
    }

    setLoading(true);
    try {
      await axios.put(`${API}/settings`, settings, { headers: getAdminHeaders() });
      toast.success('Configurações atualizadas com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast.error('Erro ao atualizar configurações');
    } finally {
      setLoading(false);
    }
  };

  const criterios = [
    { key: 'devocional', label: 'Devocional (Diário)', description: 'Pontos por fazer devocional diariamente', type: 'daily' },
    { key: 'presenca', label: 'Presença (Semanal)', description: 'Pontos por presença na reunião', type: 'weekly' },
    { key: 'pontualidade', label: 'Pontualidade (Semanal)', description: 'Pontos por chegar no horário', type: 'weekly' },
    { key: 'biblia', label: 'Bíblia (Semanal)', description: 'Pontos por trazer a Bíblia', type: 'weekly' },
    { key: 'visita', label: 'Visita (Semanal)', description: 'Pontos por fazer visita', type: 'weekly' },
    { key: 'versiculo', label: 'Versículo (Semanal)', description: 'Pontos por recitar versículo', type: 'weekly' }
  ];

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-24">
      <div className="bg-[#1A4D2E] text-white px-6 py-6">
        <div className="max-w-3xl mx-auto">
          <button
            data-testid="back-button"
            onClick={() => navigate(-1)}
            className="mb-4 hover:bg-white/10 rounded-full p-2 transition-all"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-3">
            <SettingsIcon className="w-7 h-7" strokeWidth={2.5} />
            <h1 className="font-nunito font-bold text-3xl">Configurações de Pontuação</h1>
          </div>
          <p className="font-inter text-white/80 text-sm mt-2">
            Defina quantas sementes cada critério vale
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <Card className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-[#E1ECE5]">
          <div className="flex items-center gap-3 mb-6 pb-6 border-b border-gray-200">
            <div className="bg-[#F2F7F4] rounded-full p-3">
              <Sprout className="w-6 h-6 text-[#1A4D2E]" />
            </div>
            <div>
              <h2 className="font-nunito font-bold text-xl text-[#1A4D2E]">
                Valores das Sementes
              </h2>
              <p className="font-inter text-sm text-gray-500">
                Alterações afetam apenas novas atividades
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Atividades Diárias */}
              <div className="md:col-span-2 pt-4 pb-2 border-b border-gray-200">
                <h3 className="font-nunito font-bold text-lg text-[#1A4D2E]">
                  📅 Atividades Diárias
                </h3>
                <p className="font-inter text-sm text-gray-500">
                  Marcadas pelos pais todos os dias
                </p>
              </div>
              
              {criterios.filter(c => c.type === 'daily').map((criterio) => (
                <div key={criterio.key} className="space-y-2">
                  <Label htmlFor={criterio.key} className="font-inter font-semibold text-[#1A4D2E]">
                    {criterio.label}
                  </Label>
                  <p className="font-inter text-xs text-gray-500 mb-2">
                    {criterio.description}
                  </p>
                  <div className="relative">
                    <Input
                      id={criterio.key}
                      name={criterio.key}
                      type="number"
                      min="0"
                      max="100"
                      data-testid={`input-${criterio.key}`}
                      value={settings[criterio.key]}
                      onChange={handleChange}
                      className="rounded-xl border-gray-200 focus:ring-2 focus:ring-[#1A4D2E] focus:border-[#1A4D2E] bg-gray-50/50 pr-20"
                      required
                    />
                    <span className="absolute right-4 top-1/2 transform -translate-y-1/2 font-inter text-sm text-gray-500">
                      sementes
                    </span>
                  </div>
                </div>
              ))}

              {/* Atividades Semanais */}
              <div className="md:col-span-2 pt-6 pb-2 border-b border-gray-200">
                <h3 className="font-nunito font-bold text-lg text-[#1A4D2E]">
                  📊 Atividades Semanais
                </h3>
                <p className="font-inter text-sm text-gray-500">
                  Marcadas pelos líderes nas reuniões
                </p>
              </div>

              {criterios.filter(c => c.type === 'weekly').map((criterio) => (
                <div key={criterio.key} className="space-y-2">
                  <Label htmlFor={criterio.key} className="font-inter font-semibold text-[#1A4D2E]">
                    {criterio.label}
                  </Label>
                  <p className="font-inter text-xs text-gray-500 mb-2">
                    {criterio.description}
                  </p>
                  <div className="relative">
                    <Input
                      id={criterio.key}
                      name={criterio.key}
                      type="number"
                      min="0"
                      max="100"
                      data-testid={`input-${criterio.key}`}
                      value={settings[criterio.key]}
                      onChange={handleChange}
                      className="rounded-xl border-gray-200 focus:ring-2 focus:ring-[#1A4D2E] focus:border-[#1A4D2E] bg-gray-50/50 pr-20"
                      required
                    />
                    <span className="absolute right-4 top-1/2 transform -translate-y-1/2 font-inter text-sm text-gray-500">
                      sementes
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Preview Card */}
            <Card className="bg-gradient-to-r from-[#F2F7F4] to-[#FEFAE6] rounded-xl p-6 border border-[#1A4D2E]/20">
              <h3 className="font-nunito font-bold text-lg text-[#1A4D2E] mb-4">
                Prévia dos Valores
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                {criterios.map((criterio) => (
                  <div key={criterio.key} className="flex items-center justify-between bg-white/60 rounded-lg p-3">
                    <span className="font-inter text-gray-700">{criterio.label}:</span>
                    <span className="font-nunito font-bold text-[#1A4D2E]">
                      {settings[criterio.key]}
                    </span>
                  </div>
                ))}
              </div>
            </Card>

            {/* Submit Button */}
            <Button
              type="submit"
              data-testid="submit-button"
              disabled={loading}
              className="w-full bg-[#1A4D2E] hover:bg-[#145A32] text-white rounded-full py-6 text-lg font-semibold shadow-md active:scale-95 transition-all flex items-center justify-center gap-2"
            >
              <Save className="w-5 h-5" />
              {loading ? 'Salvando...' : 'Salvar Configurações'}
            </Button>

            <p className="text-center font-inter text-xs text-gray-500">
              💡 Dica: Valores mais altos motivam mais as crianças em atividades específicas
            </p>
          </form>
        </Card>

        <ResetTemporada />
      </div>

      <Navigation active="configuracoes" />
    </div>
  );
};

export default Configuracoes;
